import { Form } from "@unform/web";
import { useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { albumExtensions } from "Helpers/File";

import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import FileUploader from "Components/Admin/FileUploader";
import Button from "Components/Global/Buttons";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

import { useTheme } from "styled-components";

// Envio para o ÁLBUM (B05 — D15/D16 da parte 2, 03/09/2026): arquivo da lista
// ampla OU link externo (YouTube/Vimeo). O link é o caminho recomendado para
// vídeo grande — é o "sugerir link ... para que evite enviar arquivos grandes"
// do PDF —, e por isso vem junto no mesmo modal, não escondido em outra tela.
const GalleryUpload = ({ projectId, setOpenModal, reload }) => {
  const theme = useTheme();

  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();

  const [files, setFiles] = useState([]);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState(null);
  // Progresso e cancelamento do envio (D21): é a tela do arquivo grande.
  const [progress, setProgress] = useState(null);
  const [abortController, setAbortController] = useState(null);

  const submitLink = async () => {
    setLinkError(null);

    // Eco da validação do backend: protocolo em lista fechada, senão o `href`
    // da tela aceitaria `javascript:`.
    if (!/^https?:\/\//i.test(linkUrl.trim())) {
      setLinkError("Informe um link começando com http:// ou https://");
      return;
    }

    if (linkName.trim().length < 2) {
      setLinkError("Informe um nome para o link");
      return;
    }

    setLoading(true);

    try {
      await api.post("/projects/album/link", {
        project_id: projectId,
        name: linkName.trim(),
        url: linkUrl.trim(),
      });

      setLoading(false);
      showNotification("success", "Sucesso!", "Link cadastrado com sucesso!");

      if (reload) reload();

      setOpenModal(null);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  const submit = async (data) => {
    setLoading(true);

    try {
      const files_to_send = [];

      const controller = new AbortController();
      setAbortController(controller);
      setProgress(0);

      for (const file of files) {
        const image_location = file.file_src
          ? file.file_src
          : await uploadFile(file?.file, undefined, {
              onProgress: setProgress,
              signal: controller.signal,
            });

        files_to_send.push({
          id: file?.id,
          name: file.name,
          file_src: image_location,
        });
      }

      setProgress(null);
      setAbortController(null);

      await api.post("/projects/gallery/upload", {
        ...data,
        project_id: projectId,
        files: files_to_send,
      });

      setLoading(false);

      showNotification("success", "Sucesso!", "Imagem cadastrada com sucesso!");

      if (reload) reload();

      setOpenModal(null);
    } catch (error) {
      setLoading(false);
      setProgress(null);
      setAbortController(null);

      // Cancelar é decisão do usuário: não é erro para notificar.
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError")
        return;

      return HTTPErrorCallback(error);
    }
  };

  return (
    <S.Wrapper>
      <S.Modal>
        <Form onSubmit={submit}>
          <S.Section>
            <span>Enviar arquivo</span>
            <hr />
          </S.Section>

          <FileUploader
            allowMultiple={false}
            label="Arquivos"
            loading={loading}
            allowedExtensions={albumExtensions}
            progress={progress}
            setFiles={(file) =>
              setFiles((prev) => [
                ...prev,
                {
                  file: file[0],
                  name: file[0].name,
                },
              ])
            }
          />

          {files.map((file) => (
            <S.File key={file.name}>
              <S.FileInfos>
                {file.file_src ? (
                  <a href={file.file_src} rel="noreferrer" target="_blank">
                    {file.name}
                  </a>
                ) : (
                  <h2>{file.name}</h2>
                )}

                <GetIcon
                  name="X"
                  color={theme.colors.neutral.white}
                  onClick={() =>
                    setFiles((prev) => prev.filter((f) => f.name !== file.name))
                  }
                />
              </S.FileInfos>
            </S.File>
          ))}

          <S.Section>
            <span>Ou adicionar link</span>
            <hr />
          </S.Section>

          <S.LinkHint>
            Para vídeos grandes, prefira publicar no YouTube ou Vimeo e colar o
            link aqui — o envio é imediato e não consome o limite de arquivo.
          </S.LinkHint>

          <S.LinkFields>
            <input
              type="text"
              placeholder="Nome do link (ex.: Making of do espetáculo)"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
            />
            <input
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />

            {linkError ? <S.LinkError>{linkError}</S.LinkError> : null}

            <Button
              type="button"
              outline
              text="Adicionar link"
              disabled={loading || !linkName || !linkUrl}
              onClick={submitLink}
            />
          </S.LinkFields>

          {abortController ? (
            <S.Buttons>
              <Button
                type="button"
                outline
                text="Cancelar envio"
                onClick={() => abortController.abort()}
              />
            </S.Buttons>
          ) : null}

          <S.Buttons>
            <Button
              type="button"
              outline
              text="Fechar"
              disabled={loading}
              onClick={() => setOpenModal(null)}
            />

            <Button type="submit" text="Enviar" disabled={loading} />
          </S.Buttons>
        </Form>
      </S.Modal>
    </S.Wrapper>
  );
};

export default GalleryUpload;
